import { apiClient, handleApiResponse } from './client'
import type { FunnelConfig, FunnelStep, ApiResponse } from '@/types'

export interface SaveFunnelConfigRequest {
  storeId: string
  steps: FunnelStep[]
  sessionTimeout: number
  attributionWindow: number
}

export interface ProductsOriginsTimelinePoint {
  date: string
  snapshot_type: string
  window_start: string
  window_end: string
  total_sessions: number
  // Per-day web revenue + conversions (P3 revenue trend). 0 on snapshots predating the field.
  revenue?: number
  conversions?: number
  products: Array<{
    product_id: string
    product_name: string
    views: number
    conversions: number
    revenue: number
    conversion_rate: number
  }>
  origins: Record<
    'google' | 'google_paid' | 'facebook' | 'instagram' | 'meta_ads' | 'direct' | 'other_search' | 'other_external' | 'other_paid',
    { sessions: number; conversions: number; revenue?: number }
  >
  // Per-day device slice (mobile/desktop/unknown), same shape as origins. Zero buckets on
  // snapshots predating `device_breakdown` (no legacy reconstruction). `revenue` is 0 on
  // snapshots predating Phase 2 (per-dimension revenue) — treat absent as 0.
  devices?: Record<'mobile' | 'desktop' | 'unknown', { sessions: number; conversions: number; revenue?: number }>
  // Origin-capture coverage for the day — how many sessions carried a real client
  // `origin` vs fell back to referrer. null on snapshots predating the metric (treat
  // as "unknown", not 0%). Lets the UI gauge how trustworthy the paid/organic split is.
  origins_coverage?: {
    sessions_total: number
    sessions_with_origin: number
    sessions_with_clickid: number
    sessions_with_utm: number
  } | null
  // GA4-style channel rollup (one-per-session), keyed by GA4 channel name plus a reserved
  // `unattributed` entry (orphaned conversions). sessions_with_origin = per-channel coverage.
  channels?: Record<string, { sessions: number; conversions: number; sessions_with_origin: number; revenue?: number }> | null
  // How this day's channels were derived: native P1 field, reconstructed from origins, or unattributed dump.
  channels_source?: 'native' | 'reconstructed' | 'unattributed'
  // Per-day campaign drill-down (for the chart's Campaña view). null on days predating P2.
  breakdown?: Record<string, {
    sources: Record<string, { sessions: number; conversions: number }>
    campaigns: Array<{ campaign: string; medium: string | null; sessions: number; conversions: number }>
    untagged: { sessions: number; conversions: number }
    other: { sessions: number; conversions: number }
  }> | null
}

export interface ProductsOriginsTimelineResponse {
  store_id: string
  window_days: number
  window_start: string
  window_end: string
  series: ProductsOriginsTimelinePoint[]
  products_index: Array<{
    product_id: string
    product_name: string
    total_views: number
    total_conversions: number
    total_revenue: number
  }>
  // Channel-data coverage across the window: days with native P1 channels vs reconstructed
  // from origins vs unattributed. Drives the table's coverage explainer.
  traffic_channels_coverage?: { native: number; reconstructed: number; unattributed: number }
  // GA4 Default Channel Group session totals — reconciliation reference, from the LATEST
  // snapshot (GA4 is a fixed rolling 7-day total; not summed across the window). null if no GA4.
  ga4_channel_totals?: Record<string, { sessions: number }> | null
  // Window-aggregated campaign drill-down (P2), keyed by channel. Empty/absent where no
  // campaign data exists yet (ramps post-deploy; can't be reconstructed from origins).
  traffic_origins_breakdown?: Record<string, {
    sources: Record<string, { sessions: number; conversions: number }>
    campaigns: Array<{ campaign: string; medium: string | null; sessions: number; conversions: number }>
    untagged: { sessions: number; conversions: number }
    other: { sessions: number; conversions: number }
  }>
}

export interface TrackFunnelStepRequest {
  experiment_id: string
  step_id: string
  step_type: 'pageview' | 'event' | 'conversion'
  user_id: string
  session_id?: string
  url?: string
  properties?: Record<string, any>
}

class FunnelService {
  // GA4 store-level funnel analytics (if configured)
  async getGa4StoreFunnelAnalytics(storeId: string, windowDays = 7) {
    const res = await apiClient.get<ApiResponse<any>>(
      '/api/analytics/ga4/store/funnel',
      { store_id: storeId, window: windowDays }
    )
    return handleApiResponse(res)
  }
  // Store-level funnel analytics
  async getStoreFunnelAnalytics(storeId: string, windowDays = 7) {
    const res = await apiClient.get<ApiResponse<any>>(
      '/api/store/funnel/analytics',
      { store_id: storeId, window: windowDays }
    )
    return handleApiResponse(res)
  }

  // Per-day timeline of product performance + origin (entry source) counts.
  // Shape: { series: [{ date, products: [...], origins: {...} }], products_index: [...] }
  async getStoreProductsOriginsTimeline(storeId: string, windowDays = 7) {
    const res = await apiClient.get<ApiResponse<ProductsOriginsTimelineResponse>>(
      '/api/store/funnel/analytics',
      { store_id: storeId, window: windowDays, timeline: 'products_origins' }
    )
    return handleApiResponse(res)
  }

  // Experiment-level funnel analytics
  async getExperimentFunnelAnalytics(experimentId: string, windowDays = 7) {
    const res = await apiClient.get<ApiResponse<any>>(
      `/api/experiments/${experimentId}/funnel`,
      { window: windowDays }
    )
    return handleApiResponse(res)
  }

  // Multi-entry funnel analytics (new for MULTI_ENTRY_FUNNEL feature)
  async getMultiEntryFunnelAnalytics(experimentId: string, windowDays = 7) {
    const res = await apiClient.get<ApiResponse<any>>(
      `/api/experiments/${experimentId}/funnel/multi-entry`,
      { window: windowDays }
    )
    return handleApiResponse(res)
  }
  // Get store-level funnel configuration
  async getFunnelConfig(storeId: string): Promise<FunnelConfig> {
    const response: ApiResponse<any> = await apiClient.get(
      '/api/store/funnel',
      { store_id: storeId }
    )
    const apiResponse = handleApiResponse(response)

    // Extract default_funnel from API response and map to expected FunnelConfig format
    const defaultFunnel = apiResponse.default_funnel
    if (!defaultFunnel || !defaultFunnel.steps) {
      throw new Error('No default funnel configuration found')
    }

    return {
      steps: defaultFunnel.steps,
      sessionTimeout: 30, // Default value
      attributionWindow: 7 // Default value
    }
  }

  // Save store-level funnel configuration
  async saveFunnelConfig(data: SaveFunnelConfigRequest): Promise<FunnelConfig> {
    const response: ApiResponse<FunnelConfig> = await apiClient.post(
      '/api/store/funnel',
      data
    )
    return handleApiResponse(response)
  }

  // Track individual funnel step
  async trackFunnelStep(data: TrackFunnelStepRequest): Promise<void> {
    const response: ApiResponse<any> = await apiClient.post(
      '/api/funnel/track',
      data
    )
    return handleApiResponse(response)
  }
}

export const funnelService = new FunnelService()
